/**
 * Reading a limit somebody typed (spec 073 FR-017, FR-018).
 *
 * Validated here, in one place, rather than in each of the three fields that accept an
 * amount — the default, an override being added, an override being changed. Three
 * copies of "is this a number" is three chances to disagree about whether `0` is one.
 *
 * The service is the authority: `SetLimitRequest` requires `gt=0` and a scale of at
 * most two decimal places, and it refuses anything else with a 422. This exists so the
 * refusal arrives *beside the field* while the reader is still looking at it, rather
 * than as a failed request after they pressed save. It is deliberately no stricter than
 * the contract, because a client that refuses what the service would accept is a bug
 * nobody can diagnose from the service's side.
 */

export type LimitAmount =
  | { readonly ok: true; readonly amount: string }
  | { readonly ok: false; readonly reason: string };

/** Digits, optionally one decimal point and at most two places after it. */
const WELL_FORMED = /^\d+(\.\d{1,2})?$/;

export function parseLimitAmount(input: string): LimitAmount {
  const trimmed = input.trim();

  if (trimmed === '') {
    // Not "invalid" — absent. Removing a limit is its own action (a delete), so an
    // empty field is an unfinished edit rather than a request to make a workspace
    // unlimited, and saying so is what stops someone clearing the box to try.
    return { ok: false, reason: 'Enter an amount.' };
  }

  // Currency symbols and thousands separators are what people paste out of a
  // spreadsheet. Refused rather than stripped: silently reinterpreting `1,50` — which
  // is one and a half in half of Europe and one hundred and fifty in the other half —
  // is how a limit ends up a hundredfold wrong with nobody having typed it.
  if (!WELL_FORMED.test(trimmed)) {
    return {
      ok: false,
      reason:
        'Use digits and at most two decimal places, with no currency symbol or separators.',
    };
  }

  if (Number(trimmed) <= 0) {
    // A limit of zero would stop every workspace it applies to, permanently and
    // silently, which is not something anyone means by "set a limit". Removing the
    // limit and blocking everything are opposite intentions and must not share a
    // spelling.
    return {
      ok: false,
      reason: 'A limit must be more than zero. To remove a limit, remove it.',
    };
  }

  // Returned as the string that was typed, not as a number. The contract carries money
  // as a string precisely so no figure passes through a float, and parsing it here to
  // "validate" it would be the one place the product reintroduced that.
  return { ok: true, amount: trimmed };
}
